import {
  hasPendingToolInvocation,
  HotboxChatThread,
  textFromMessage,
  usePaymentConfirmationPoll,
} from "@/chat-shared";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, isToolUIPart, type UIMessage } from "ai";
import { Loader2, SendHorizontal } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

function readQueryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key)?.trim() ?? "";
}

function devFallbackBackend(): string {
  if (!import.meta.env.DEV) return "";
  return `${window.location.protocol}//${window.location.hostname}:8080/api`;
}

export default function ChatWidget() {
  const chatbotId = readQueryParam("chatbotId");
  const backendBase =
    readQueryParam("backendUrl") ||
    import.meta.env.VITE_BACKEND_URL?.replace(/\/$/, "") ||
    devFallbackBackend();
  const payFrontendOrigin =
    readQueryParam("frontendUrl") ||
    import.meta.env.VITE_FRONTEND_URL?.replace(/\/$/, "") ||
    (import.meta.env.DEV ? "http://localhost:5173" : "");

  const logoUrl = readQueryParam("logoUrl");
  const storeName = readQueryParam("storeName") || "Hot Box Sauna";
  const primaryColor = readQueryParam("primaryColor") || "#0f766e";
  const defaultMessage =
    readQueryParam("defaultMessage") ||
    "Hi — I can help you check availability and book a session at The Hot Box.";

  const [input, setInput] = useState("");
  const scrollAnchorRef = useRef<HTMLDivElement>(null);

  const welcomeMessages = useMemo<UIMessage[]>(
    () => [
      {
        id: "hotbox-welcome",
        role: "assistant",
        parts: [{ type: "text", text: defaultMessage }],
      },
    ],
    [defaultMessage]
  );

  const chatApiUrl = useMemo(() => {
    const base = backendBase.replace(/\/$/, "");
    return `${base}/chat`;
  }, [backendBase]);

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: chatApiUrl,
        credentials: "omit",
        prepareSendMessagesRequest: ({ messages }) => ({
          body: { messages },
        }),
      }),
    [chatApiUrl]
  );

  const { messages, sendMessage, status, error, stop, clearError, setMessages } = useChat({
    transport,
    messages: welcomeMessages,
  });

  usePaymentConfirmationPoll(backendBase, messages, setMessages, "omit");

  const toolInvocations = useMemo(
    () =>
      messages.flatMap((message) =>
        message.role === "assistant" ? message.parts.filter((part) => isToolUIPart(part)) : []
      ),
    [messages]
  );

  const isBusy = status === "submitted" || status === "streaming";

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, status, isBusy, toolInvocations.length]);

  const lastMessage = messages[messages.length - 1];
  const lastAssistantText =
    lastMessage?.role === "assistant" ? textFromMessage(lastMessage as UIMessage) : "";
  const showTypingRow =
    isBusy &&
    !hasPendingToolInvocation(lastMessage as UIMessage | undefined) &&
    (lastMessage?.role !== "assistant" ||
      (lastMessage?.role === "assistant" && !lastAssistantText));

  async function submitMessage() {
    const text = input.trim();
    if (!text || isBusy) return;
    setInput("");
    await sendMessage({ text });
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault();
    void submitMessage();
  }

  if (!backendBase) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-50 p-4 text-center text-sm text-red-600 dark:bg-neutral-950 dark:text-red-400">
        Missing backend URL. Set{" "}
        <code className="mx-1 rounded bg-neutral-200 px-1 dark:bg-neutral-800">backendUrl</code> in the
        embed config or{" "}
        <code className="mx-1 rounded bg-neutral-200 px-1 dark:bg-neutral-800">VITE_BACKEND_URL</code>{" "}
        when building the widget.
      </div>
    );
  }

  return (
    <div
      className="flex h-full w-full flex-col bg-neutral-50 dark:bg-neutral-950"
      data-hotbox-chatbot-id={chatbotId || undefined}
    >
      <div
        className="flex shrink-0 items-center gap-3 px-4 py-3 text-white shadow-sm"
        style={{ background: primaryColor }}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-8 w-8 rounded-full bg-white/20 object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-white/20" />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">{storeName}</p>
          <p className="truncate text-xs text-white/90">Booking assistant</p>
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto pr-1">
        <div className="px-3 pt-3">
          <HotboxChatThread
            messages={messages}
            error={error}
            onDismissError={() => clearError()}
            showTypingRow={showTypingRow}
            scrollAnchorRef={scrollAnchorRef}
            emptyPlaceholder="Send a message to start."
            payFrontendOrigin={payFrontendOrigin}
          />
        </div>
      </div>

      <form
        onSubmit={handleFormSubmit}
        className="shrink-0 border-t border-neutral-200 bg-neutral-50 px-3 py-3 dark:border-neutral-800 dark:bg-neutral-950"
      >
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void submitMessage();
              }
            }}
            placeholder="Type your message…"
            rows={2}
            disabled={isBusy}
            className="min-h-[44px] flex-1 resize-none rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 outline-none ring-offset-white focus-visible:ring-2 focus-visible:ring-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-50 dark:ring-offset-neutral-950"
            aria-label="Message"
          />
          <div className="flex shrink-0 flex-col gap-2">
            <button
              type="submit"
              disabled={!input.trim() || isBusy}
              className="inline-flex h-10 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium text-white disabled:pointer-events-none disabled:opacity-50"
              style={{ background: primaryColor }}
            >
              <SendHorizontal className="size-4 shrink-0" aria-hidden />
            </button>
            {isBusy ? (
              <button
                type="button"
                onClick={() => void stop()}
                className="inline-flex h-9 items-center justify-center rounded-md border border-neutral-200 bg-white px-2 text-xs font-medium text-neutral-900 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-50 dark:hover:bg-neutral-800"
              >
                Stop
              </button>
            ) : null}
          </div>
        </div>
        {isBusy ? (
          <p className="mt-2 flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <Loader2 className="size-3.5 animate-spin" aria-hidden />
            Working…
          </p>
        ) : null}
      </form>
    </div>
  );
}
